import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class ProjectService {
  constructor(private _api: ApiService) {}

  getProjectDetailsById(id: string): Observable<any> {
    return this._api.get(`/project/${id}`).pipe(map((data) => data));
  }

  getProjectDetailsByUriLabel(uriLabel: string): Observable<any> {
    return this._api.get(`/project?query=uriLabel:${uriLabel}`).pipe(map((data) => data));
  }

  getProjectDetails(uriLabel: string, page: number, perPage: number, sort = 'created', ascending = false): Observable<any> {
    return this._api
      .get(`/project?query=uriLabel:${uriLabel}&limit=${perPage}&offset=${page}&field=name&sort=${sort}&ascending=${ascending}`)
      .pipe(map((data: any) => data));
  }

  getProjectsDetails(
    query: string,
    page: number,
    perPage: number,
    sort = 'name',
    ascending = true,
    showPublic = true,
    handler = '',
  ): Observable<any> {
    const results = this._api
      .get(
        `/project?query=${encodeURIComponent(query)}&limit=${perPage}&offset=${page}&sort=${sort}&ascending=${ascending}&public=${showPublic}${handler ? `&handler=${handler}` : ''}`,
      )
      .pipe(map((data) => data));
    return results;
  }

  getProjectByUriLabel(uriLabel: string, showPublic = false): Observable<any> {
    const results = this._api.get(`/project?query=uriLabel:${uriLabel}&public=${showPublic}`).pipe(map((data) => data));
    return results;
  }

  getOrganizationSpecificProjects(
    organizationId: string,
    searchProject = '',
    page: number,
    perPage: number,
    sort: string,
    ascending = true,
  ): Observable<any> {
    const results = this._api
      .get(
        `/project?query=organizationId:${organizationId}${
          searchProject || searchProject.length > 0 ? ` AND ${searchProject}` : ''
        }&limit=${perPage}&offset=${page}&field=name&sort=${sort}&ascending=${ascending}`,
      )
      .pipe(map((data) => data));
    return results;
  }

  getProjectTerminologies(projectId: string): Observable<any> {
    return this._api.get(`/project/${projectId}/terminology`).pipe(map((data) => data));
  }

  getTerminologyFamily(): Observable<any> {
    const results = this._api.get(`/config/family?limit=100000`).pipe(map((data) => data));
    return results;
  }

  getTerminologyFamilyDetails(string: string): Observable<any> {
    const results = this._api.get(`/terminology?limit=100000&sort=version&ascending=false&query=family:${string}`).pipe(map((data) => data));
    return results;
  }

  getLatestTerminologies(): Observable<any> {
    const results = this._api.get(`/terminology?limit=100000&query=latest:true`).pipe(map((data) => data));
    return results;
  }

  getLatestTerminologiesWith(abbreviation: string, publisher: string, sort = 'releaseDate', ascending = 'true'): Observable<any> {
    const results = this._api
      .get(`/terminology?query=abbreviation:${abbreviation}+AND+publisher:${publisher}&limit=100000&sort=${sort}&ascending=${ascending}`)
      .pipe(map((data) => data));
    return results;
  }

  addProject(
    name: string,
    description: string,
    uriLabel: string,
    publicPrivate: boolean,
    organizationId: string,
    terminologies: any,
    mapsets: any,
    subsets: any,
  ): Observable<any> {
    const results = this._api
      .post(`/project`, {
        name,
        description,
        uriLabel,
        public: publicPrivate,
        organizationId,
        terminologies,
        mapsets,
        subsets,
      })
      .pipe(map((data) => data));
    return results;
  }

  generateProjectApiKey(project: any): Observable<any> {
    const results = this._api
      .post(`/project/apiKey`, {
        ...project,
      })
      .pipe(map((data) => data));
    return results;
  }

  addUserToProject(projectId: string, userId: string, role: string): Observable<any> {
    const body = {
      userId: userId,
    };
    const results = this._api.post(`/project/${projectId}/${role.toLowerCase()}`, body).pipe(map((data) => data));
    return results;
  }

  removeUserFromProject(project: any, userId: string, userRole: string) {
    let role: string;
    let owners: string[] = [];
    owners = project?.owners;
    if (owners?.indexOf(userId) >= 0) {
      role = 'owner';
    } else {
      role = 'member';
    }

    const results = this._api.delete(`/project/${project.id}/${userRole || role}/${userId}`).pipe(map((data) => data));
    return results;
  }

  removeProjectUser(project: any, userId: string, userRole: string) {
    const results = this._api.delete(`/project/${project.id}/${userRole}/user/${userId}`).pipe(map((data) => data));
    return results;
  }

  addTerminologiesToProject(projectId: string, terminology: any): Observable<any> {
    const results = this._api.post(`/project/${projectId}/terminologies`, terminology).pipe(map((data) => data));
    return results;
  }

  removeTerminologiesFromProject(project: any, terminologies: any) {
    const publisher = terminologies?.publisher;
    const abbreviation = terminologies?.abbreviation;
    const version = terminologies?.version;

    const results = this._api.delete(`/project/${project.id}/terminologies/${publisher}/${abbreviation}/${version}`).pipe(map((data) => data));
    return results;
  }

  updateProject(
    id: string,
    name: string,
    description: string,
    uriLabel: string,
    publicPrivate: any,
    organizationId: string,
    terminologies: any,
    apiKey: any,
  ): Observable<any> {
    const results = this._api
      .patch(`/project/${id}`, {
        ...(name ? { name } : null),
        ...(description ? { description } : null),
        ...(uriLabel ? { uriLabel } : null),
        ...(publicPrivate ? { public: publicPrivate } : null),
        ...(organizationId ? { organizationId } : null),
        ...(terminologies ? { terminologies } : null),
        apiKey,
      })
      .pipe(map((data) => data));
    return results;
  }

  deleteProject(id: string): Observable<any> {
    const results = this._api.delete(`/project/${id}`).pipe(map((data) => data));
    return results;
  }

  getProjectTeams(id: string, offset = 0, limit = 10, query?: string, sort?: string, ascending = false, handler = ''): Observable<any> {
    const eq = query ? encodeURIComponent(query) : '';
    return this._api
      .get(
        `/project/${id}/teams?query=${eq}&offset=${offset}&limit=${limit}&${sort ? `sort=${sort}&` : ''}ascending=${ascending}${handler ? `&handler=${handler}` : ''}`,
      )
      .pipe(map((data) => data));
  }

  getProjectTeamsById(id: string): Observable<any> {
    const results = this._api.get(`/project/${id}/teams`).pipe(map((data) => data));
    return results;
  }

  getProjectUsers(id: string): Observable<any> {
    const results = this._api.get(`/project/${id}/users`).pipe(map((data) => data));
    return results;
  }

  checkProjectDuplicate(uriLabel?: string, projectName?: string, organizationId?: string, projectId?: string): Observable<any> {
    const prjId = projectId ? `&projectId=${projectId}` : null;
    const prjName = projectName ? `&projectName=${encodeURIComponent(projectName)}` : null;
    const prjuriLabel = uriLabel ? `&uriLabel=${uriLabel}` : null;
    const results = this._api
      .postWithoutAuth(
        `/project/check?${prjName ? prjName : ''}${prjuriLabel ? prjuriLabel : ''}${prjId ? prjId : ''}&organizationId=${organizationId}`,
        {},
      )
      .pipe(map((data) => data));
    return results;
  }

  getOrganizationTeams(id: string, offset = 0, limit = 10, query?: string, sort?: string, ascending = false): Observable<any> {
    const eq = query ? encodeURIComponent(query) : '';
    return this._api
      .get(`/organization/${id}/teams?query=${eq}&offset=${offset}&limit=${limit}&${sort ? `sort=${sort}&` : ''}ascending=${ascending}`)
      .pipe(map((data) => data));
  }

  replaceProject(project: any): Observable<any> {
    return this._api.put(`/project/${project.id}`, project).pipe(map((data) => data));
  }

  getProjectPreviewBulk(projectId: string, mode: string, data: string): Observable<any> {
    const results = this._api.post(`/project/${projectId}/preview/bulk?mode=${mode}`, data).pipe(map((data) => data));

    return results;
  }
  getAvailableFormats(): Observable<any> {
    const results = this._api.get(`/config/format`).pipe(map((data) => data));
    return results;
  }
}
